/**
 * BrushBuilder - Brush Entity 생성을 위한 Builder
 */
import type { ICanvasEngineEntityController } from '../../../types/interface'
import type { Vector2d, CanvasEngineExportEntityType } from '../../../types'
import { SHAPES } from '../../../const'
import type { BrushInitConfig, BrushLineData } from './brushEntity'

/**
 * BrushBuilder 클래스
 */
export class BrushBuilder {
  private canvasEngine: ICanvasEngineEntityController
  private config: Partial<BrushInitConfig> = {}

  constructor(canvasEngine: ICanvasEngineEntityController) {
    this.canvasEngine = canvasEngine
    this.reset()
  }

  reset(): BrushBuilder {
    this.config = { interactionMode: true }
    return this
  }

  setId(id: string): BrushBuilder {
    this.config.id = id
    return this
  }

  setCreatePoint(point: Vector2d): BrushBuilder {
    this.config.createPoint = point
    return this
  }

  setBalloonPoint(point: Vector2d): BrushBuilder {
    this.config.balloonPoint = point
    return this
  }

  setColor(color: string): BrushBuilder {
    if (!this.config.config) {
      this.config.config = { color }
    } else {
      this.config.config.color = color
    }
    return this
  }

  setWidth(width: number): BrushBuilder {
    if (!this.config.config) {
      this.config.config = { color: '#000000', width }
    } else {
      this.config.config.width = width
    }
    return this
  }

  setPoints(points: number[]): BrushBuilder {
    this.config.points = points
    return this
  }

  setRotation(rotation: number): BrushBuilder {
    this.config.rotation = rotation
    return this
  }

  setLayerId(layerId: string): BrushBuilder {
    this.config.layerId = layerId
    return this
  }

  setBrushLines(brushLines: BrushLineData[]): BrushBuilder {
    this.config.brushLines = brushLines
    return this
  }

  setInteractionMode(mode: boolean): BrushBuilder {
    this.config.interactionMode = mode
    return this
  }

  fromExportData(data: CanvasEngineExportEntityType): BrushBuilder {
    if (data.type !== SHAPES.BRUSH) {
      throw new Error('Invalid entity type for BrushBuilder')
    }

    this.setId(data.shapeId)
      .setCreatePoint({ x: 0, y: 0 })
      .setBalloonPoint(data.balloonPoint)
      .setColor(data.color)
      .setLayerId(data.layerId)

    if (data.points) this.setPoints(data.points)
    if (data.width) this.setWidth(data.width)
    if (data.rotate !== undefined) this.setRotation(data.rotate)

    // brushLines 변환
    if (data.brushLines && data.brushLines.length > 0) {
      const brushLines: BrushLineData[] = data.brushLines.map(line => ({
        points: line.points,
        strokeWidth: line.strokeWidth,
        stroke: data.color || '#000000',
      }))
      this.setBrushLines(brushLines)
    } else if (data.points && data.points.length > 0) {
      // 기존 브러시 호환성
      this.setBrushLines([
        {
          points: data.points,
          strokeWidth: data.width || 1,
          stroke: data.color || '#000000',
        },
      ])
    }

    return this
  }

  build(): BrushInitConfig {
    if (!this.config.createPoint) {
      throw new Error('createPoint is required')
    }
    if (!this.config.config) {
      throw new Error('config is required')
    }

    return {
      id: this.config.id,
      createPoint: this.config.createPoint,
      balloonPoint: this.config.balloonPoint,
      config: this.config.config,
      points: this.config.points,
      rotation: this.config.rotation,
      layerId: this.config.layerId,
      brushLines: this.config.brushLines,
      interactionMode: this.config.interactionMode,
    }
  }

  getCanvasEngine(): ICanvasEngineEntityController {
    return this.canvasEngine
  }
}
